import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { usePmTestMode } from "../hooks/usePmTestMode";
import {
  Plus, Search, Filter, Users, Mail, Phone, Clock, ChevronRight,
  MoreHorizontal, Edit2, Trash2, ArrowUpDown, Globe, Building, User,
  Briefcase, Building2, BookOpen, DollarSign, Star, MapPin, Send, Archive, RotateCcw, Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const RESOURCE_TYPES = [
  { value: "ALL", label: "All Resources", icon: Users },
  { value: "ARTIST", label: "Artists", icon: User },
  { value: "PROJECT_MANAGER", label: "Project Managers", icon: Briefcase },
  { value: "STUDIO", label: "Studios", icon: Building },
  { value: "VENDOR", label: "Vendors", icon: Building2 },
];

const EMPLOYMENT_TYPES = ["Employee", "Contractor", "Freelancer", "Agency"];
const STATUSES = ["Active", "On Leave", "Inactive"];
const WAGE_TYPES = ["Hourly", "Project-based", "Retainer", "Salary"];
const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD"];
const SPECIALTIES = ["Interior Design Lead", "Studio Director", "COO", "Admin", "Director of Marketing", "Asset Management"];
const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "Europe/London", "Europe/Paris", "Asia/Tokyo", "Australia/Sydney"
];

export default function ResourceCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const pmTestMode = usePmTestMode();
  const isPM = user?.role === "Program Manager" || user?.role === "Marketing PM" || pmTestMode?.isActive;
  const isArtist = user?.role === "Artist";
  const canViewFinancials = !isPM && !isArtist && ["COO", "Admin", "Accountant", "Sales"].includes(user?.role);
  const canInvite = user?.role === "COO" || user?.role === "Admin";

  // Data states
  const [resources, setResources] = useState([]);
  const [archivedResources, setArchivedResources] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [yellowPages, setYellowPages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [activeTab, setActiveTab] = useState("ALL");
  const [showArchived, setShowArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("display_name");
  const [sortDirection, setSortDirection] = useState("asc");

  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [inviteArtist, setInviteArtist] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  
  // Bulk selection states
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Recovery states
  const [showRecoverDialog, setShowRecoverDialog] = useState(false);
  const [orphanedResources, setOrphanedResources] = useState([]);
  const [selectedOrphan, setSelectedOrphan] = useState(null);
  const [recoverForm, setRecoverForm] = useState({
    display_name: "", email: "", resource_type: "ARTIST",
    specialty: "", employment_type: "In-house", wage_type: "Retainer",
    wage_amount: 0, currency: "USD", contracted_hours_monthly: 160
  });
  const [recovering, setRecovering] = useState(false);
  
  const [formData, setFormData] = useState({
    resource_type: "ARTIST",
    display_name: "",
    legal_name: "",
    email: "",
    phone: "",
    timezone: "America/New_York",
    status: "Active",
    specialty: "",
    employment_type: "Contractor",
    wage_type: "Hourly",
    wage_amount: "",
    currency: "USD",
    default_hours_per_day: 8,
    notes: "",
    // Vendor-specific
    company_name: "",
    contact_name: "",
    website: "",
    services: [],
    external_links: [], // Multi-entry external URLs
    // Yellow Pages
    category: "",
    description: "",
    location: "",
    rating: 0
  });

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resourcesRes, vendorsRes, yellowPagesRes] = await Promise.all([
        apiClient.get("/resources"),
        apiClient.get("/vendors").catch(() => ({ data: [] })),
        apiClient.get("/yellow-pages").catch(() => ({ data: [] }))
      ]);
      
      setResources(resourcesRes.data || []);
      setVendors(vendorsRes.data || []);
      setYellowPages(yellowPagesRes.data || []);
    } catch (err) {
      toast.error("Failed to load resources");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    fetchArchivedResources();
  }, [fetchData]);

  // Combined and filtered data
  const allResources = useMemo(() => {
    // Combine all resource types
    const combined = [
      ...resources.map(r => ({ ...r, _source: "resource" })),
      ...vendors.map(v => ({ 
        ...v, 
        _source: "vendor",
        resource_type: "VENDOR",
        display_name: v.company_name || v.name,
        status: v.status || "Active"
      })),
      ...yellowPages.map(yp => ({
        ...yp,
        _source: "yellow_pages",
        resource_type: "YELLOW_PAGES",
        display_name: yp.name || yp.company_name,
        status: "Active"
      }))
    ];

    // Filter by type
    let filtered = combined;
    if (activeTab !== "ALL") {
      filtered = combined.filter(r => r.resource_type === activeTab);
    }

    // Filter by search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.display_name?.toLowerCase().includes(term) ||
        r.legal_name?.toLowerCase().includes(term) ||
        r.email?.toLowerCase().includes(term) ||
        r.specialty?.toLowerCase().includes(term) ||
        r.company_name?.toLowerCase().includes(term)
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(r => r.status === statusFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      const aVal = a[sortField] || "";
      const bVal = b[sortField] || "";
      const comparison = aVal.toString().localeCompare(bVal.toString());
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return filtered;
  }, [resources, vendors, yellowPages, activeTab, searchTerm, statusFilter, sortField, sortDirection]);

  // Stats
  const stats = useMemo(() => ({
    total: resources.length + vendors.length + yellowPages.length,
    artists: resources.filter(r => r.resource_type === "ARTIST").length,
    pms: resources.filter(r => r.resource_type === "PROJECT_MANAGER").length,
    studios: resources.filter(r => r.resource_type === "STUDIO").length,
    vendors: vendors.length,
    active: resources.filter(r => r.status === "Active").length
  }), [resources, vendors, yellowPages]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Bulk selection helpers
  const allSelected = allResources.length > 0 && selectedIds.length === allResources.length;
  const someSelected = selectedIds.length > 0;

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allResources.map(r => r.id));
    }
  }

  function toggleSelect(id) {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }

  async function handleBulkDelete() {
    setDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => 
        apiClient.delete(`/resources/${id}`)
      ));
      toast.success(`${selectedIds.length} resource(s) archived. View in Archived tab to restore.`);
      setSelectedIds([]);
      setShowDeleteConfirm(false);
      fetchData();
      fetchArchivedResources();
    } catch (error) {
      toast.error("Failed to archive some resources");
    } finally {
      setDeleting(false);
    }
  }

  const handleSave = async () => {
    try {
      // Clean up form data - convert empty strings to null for numeric fields
      const cleanedData = {
        ...formData,
        wage_amount: formData.wage_amount === "" ? null : parseFloat(formData.wage_amount) || null,
        default_hours_per_day: formData.default_hours_per_day || 8,
        // Remove empty string values for optional fields
        legal_name: formData.legal_name || null,
        email: formData.email || null,
        phone: formData.phone || null,
        specialty: formData.specialty || null,
        notes: formData.notes || null,
        company_name: formData.company_name || null,
        contact_name: formData.contact_name || null,
        website: formData.website || null,
        category: formData.category || null,
        description: formData.description || null,
        location: formData.location || null,
        rating: formData.rating || null
      };
      
      if (editingResource) {
        await apiClient.patch(`/resources/${editingResource.id}`, cleanedData);
        toast.success("Resource updated");
      } else {
        await apiClient.post("/resources", cleanedData);
        toast.success("Resource created");
      }
      setShowAddDialog(false);
      setEditingResource(null);
      resetForm();
      fetchData();
    } catch (err) {
      // Handle validation errors (422) which return an array of error objects
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Extract error messages from validation errors
        const errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
        toast.error(errorMsg || "Validation error");
      } else if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.msg || detail.message || "Failed to save resource");
      } else {
        toast.error(detail || "Failed to save resource");
      }
    }
  };

  const handleDelete = async (resource) => {
    if (!confirm(`Archive ${resource.display_name}? They will be moved to the Archived section and can be restored later.`)) return;
    try {
      await apiClient.delete(`/resources/${resource.id}`);
      toast.success(`${resource.display_name} archived. View in Archived tab to restore.`);
      fetchData();
      fetchArchivedResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to archive resource");
    }
  };

  const fetchArchivedResources = async () => {
    try {
      const res = await apiClient.get("/resources/archived");
      setArchivedResources(res.data || []);
    } catch {
      setArchivedResources([]);
    }
  };

  const handleRestore = async (resource) => {
    try {
      await apiClient.post(`/resources/${resource.id}/restore`);
      toast.success(`${resource.display_name} restored successfully`);
      fetchData();
      fetchArchivedResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore resource");
    }
  };

  const fetchOrphanedResources = async () => {
    try {
      const res = await apiClient.get("/resources/orphaned-ids");
      setOrphanedResources(res.data || []);
    } catch { setOrphanedResources([]); }
  };

  const openRecoverDialog = async () => {
    await fetchOrphanedResources();
    setShowRecoverDialog(true);
  };

  const handleRecover = async () => {
    if (!selectedOrphan || !recoverForm.display_name) return;
    setRecovering(true);
    try {
      const payload = { ...recoverForm };
      if (selectedOrphan.resource_id) {
        payload.resource_id = selectedOrphan.resource_id;
      }
      const res = await apiClient.post("/resources/recover", payload);
      const relinked = (res.data.updated_cost_logs || 0) + (res.data.updated_timeline_blocks || 0) + (res.data.updated_assignments || 0);
      toast.success(`${recoverForm.display_name} recovered!${relinked > 0 ? ` ${relinked} data entries re-linked.` : ""}`);
      setShowRecoverDialog(false);
      setSelectedOrphan(null);
      setRecoverForm({ display_name: "", email: "", resource_type: "ARTIST", specialty: "", employment_type: "In-house", wage_type: "Retainer", wage_amount: 0, currency: "USD", contracted_hours_monthly: 160 });
      fetchData();
      fetchArchivedResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to recover resource");
    } finally {
      setRecovering(false);
    }
  };

  const resetForm = () => {
    setFormData({
      resource_type: "ARTIST",
      display_name: "",
      legal_name: "",
      email: "",
      phone: "",
      timezone: "America/New_York",
      status: "Active",
      specialty: "",
      employment_type: "Contractor",
      wage_type: "Hourly",
      wage_amount: "",
      currency: "USD",
      default_hours_per_day: 8,
      notes: "",
      company_name: "",
      contact_name: "",
      website: "",
      services: [],
      external_links: [],
      category: "",
      description: "",
      location: "",
      rating: 0
    });
  };

  const openEditDialog = (resource) => {
    setEditingResource(resource);
    setFormData({
      ...formData,
      ...resource,
      external_links: resource.external_links || []
    });
    setShowAddDialog(true);
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case "ARTIST": return <User className="w-4 h-4" />;
      case "PROJECT_MANAGER": return <Briefcase className="w-4 h-4" />;
      case "STUDIO": return <Building className="w-4 h-4" />;
      case "VENDOR": return <Building2 className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      "Active": "bg-green-100 text-green-700",
      "On Leave": "bg-amber-100 text-amber-700",
      "Inactive": "bg-zinc-100 text-zinc-500"
    };
    return colors[status] || "bg-zinc-100 text-zinc-500";
  };

  return (
    <div className="space-y-6" data-testid="resource-center">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-light tracking-tight" style={{ fontFamily: 'var(--font-sans)' }}>
            Resource Center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Unified directory of all team members, studios, and vendors
          </p>
        </div>
        {!isPM && (
          <Button onClick={() => { resetForm(); setShowAddDialog(true); }} className="rounded-full">
            <Plus className="w-4 h-4 mr-2" /> Add Resource
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-blue-500/10 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="text-xl font-light">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-violet-600" />
              <div>
                <p className="text-xs text-muted-foreground">Artists</p>
                <p className="text-xl font-light">{stats.artists}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-xs text-muted-foreground">PMs</p>
                <p className="text-xl font-light">{stats.pms}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Building className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-xs text-muted-foreground">Studios</p>
                <p className="text-xl font-light">{stats.studios}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-rose-600" />
              <div>
                <p className="text-xs text-muted-foreground">Vendors</p>
                <p className="text-xl font-light">{stats.vendors}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-muted-foreground">Active</p>
                <p className="text-xl font-light">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, specialty..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Type Filter Tabs */}
            <div className="flex gap-2 flex-wrap">
              {RESOURCE_TYPES.map(type => (
                <Button
                  key={type.value}
                  variant={activeTab === type.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveTab(type.value)}
                  className="rounded-full"
                >
                  <type.icon className="w-4 h-4 mr-1" />
                  {type.label}
                </Button>
              ))}
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Archived Toggle */}
            {canInvite && (
              <button
                onClick={() => setShowArchived(!showArchived)}
                className={`h-9 px-3 rounded-full text-sm font-medium border transition-all flex items-center gap-1.5 ${
                  showArchived
                    ? "bg-amber-600 text-white border-amber-600"
                    : "bg-background text-muted-foreground border-input hover:bg-accent hover:text-foreground"
                }`}
                data-testid="toggle-archived"
              >
                <Archive className="w-4 h-4" />
                Archived
                {archivedResources.length > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${showArchived ? 'bg-white/20' : 'bg-muted'}`}>
                    {archivedResources.length}
                  </span>
                )}
              </button>
            )}

            {/* Recover Deleted Resource */}
            {canInvite && (
              <button
                onClick={openRecoverDialog}
                className="h-9 px-3 rounded-full text-sm font-medium border border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground transition-all flex items-center gap-1.5"
                data-testid="recover-resource-btn"
              >
                <RotateCcw className="w-4 h-4" />
                Recover Deleted
              </button>
            )}

            {/* Bulk Delete Button */}
            {selectedIds.length > 0 && (
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="bulk-delete-resources-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedIds.length})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Resource List */}
      <Card>
        <div className="max-h-[600px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[50px]">
                  <Checkbox 
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all resources"
                    data-testid="select-all-resources"
                  />
                </TableHead>
                <TableHead className="w-[50px]">Type</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort("display_name")}
                >
                  <div className="flex items-center gap-1">
                    Name
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Specialty</TableHead>
                {canViewFinancials && <TableHead>Rate</TableHead>}
                <TableHead>Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canViewFinancials ? 8 : 7} className="text-center py-8">
                    Loading resources...
                  </TableCell>
                </TableRow>
              ) : allResources.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canViewFinancials ? 8 : 7} className="text-center py-8 text-muted-foreground">
                    No resources found
                  </TableCell>
                </TableRow>
              ) : (
                allResources.map(resource => {
                  const isSelected = selectedIds.includes(resource.id);
                  return (
                  <TableRow 
                    key={resource.id} 
                    className={`cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-primary/5' : ''}`}
                    onClick={() => {
                      if (resource.resource_type === "ARTIST" || resource.resource_type === "PROJECT_MANAGER") {
                        navigate(`/studio/resources/${resource.id}`);
                      }
                    }}
                  >
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(resource.id)}
                        aria-label={`Select ${resource.display_name}`}
                        data-testid={`select-resource-${resource.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                        {getResourceIcon(resource.resource_type)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{resource.display_name}</p>
                        {resource.legal_name && resource.legal_name !== resource.display_name && (
                          <p className="text-xs text-muted-foreground">{resource.legal_name}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {resource.email && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Mail className="w-3 h-3" />
                            {resource.email}
                          </div>
                        )}
                        {resource.phone && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Phone className="w-3 h-3" />
                            {resource.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {resource.specialty && (
                        <Badge variant="outline" className="rounded-full text-xs">
                          {resource.specialty}
                        </Badge>
                      )}
                    </TableCell>
                    {canViewFinancials && (
                      <TableCell>
                        {resource.wage_amount && (
                          <span className="text-sm font-medium">
                            ${resource.wage_amount}/{resource.wage_type === "Hourly" ? "hr" : resource.wage_type === "Salary" ? "mo" : resource.wage_type === "Project-based" ? "proj" : "mo"}
                          </span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge className={`${getStatusBadge(resource.status)} rounded-full text-xs`}>
                        {resource.status || "Active"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(resource); }}>
                            <Edit2 className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          {!isPM && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={(e) => { e.stopPropagation(); handleDelete(resource); }}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </>
                          )}
                          {canInvite && (resource.resource_type === "ARTIST" || resource.resource_type === "PM" || resource.resource_type === "PROJECT_MANAGER") && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={(e) => { e.stopPropagation(); setInviteArtist(resource); }}
                              >
                                <Send className="w-4 h-4 mr-2" /> Invite to Platform
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );})
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Archived Resources Section */}
      {showArchived && canInvite && (
        <Card className="border-amber-200" data-testid="archived-resources-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'var(--font-sans)' }}>
              <Archive className="w-4 h-4 text-amber-600" />
              Archived Resources
              <Badge variant="outline" className="ml-2">{archivedResources.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {archivedResources.length === 0 ? (
              <p className="text-center py-6 text-muted-foreground text-sm">No archived resources</p>
            ) : (
              <div className="space-y-2">
                {archivedResources.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-sm font-medium">
                        {(r.display_name || "?").charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{r.display_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {r.resource_type} {r.archived_at ? `· Archived ${new Date(r.archived_at).toLocaleDateString()}` : ""}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestore(r)}
                      className="text-xs"
                      data-testid={`restore-${r.id}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingResource ? "Edit Resource" : "Add New Resource"}</DialogTitle>
            <DialogDescription>
              {editingResource ? "Update resource information" : "Add a new team member, studio, or vendor"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Resource Type</Label>
              <Select 
                value={formData.resource_type} 
                onValueChange={(v) => setFormData({ ...formData, resource_type: v })}
                disabled={!!editingResource}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ARTIST">Artist</SelectItem>
                  <SelectItem value="PROJECT_MANAGER">Project Manager</SelectItem>
                  <SelectItem value="STUDIO">Studio</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Display Name *</Label>
              <Input
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Legal Name</Label>
              <Input
                value={formData.legal_name}
                onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                placeholder="John Michael Doe"
              />
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1 555 123 4567"
              />
            </div>

            <div>
              <Label>Specialty</Label>
              <Select 
                value={formData.specialty} 
                onValueChange={(v) => setFormData({ ...formData, specialty: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select specialty" />
                </SelectTrigger>
                <SelectContent>
                  {SPECIALTIES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Employment Type</Label>
              <Select 
                value={formData.employment_type} 
                onValueChange={(v) => setFormData({ ...formData, employment_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYMENT_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Timezone</Label>
              <Select 
                value={formData.timezone} 
                onValueChange={(v) => setFormData({ ...formData, timezone: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(tz => (
                    <SelectItem key={tz} value={tz}>{tz.split("/")[1]?.replace("_", " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {canViewFinancials && (
              <>
                <div>
                  <Label>Wage Type</Label>
                  <Select 
                    value={formData.wage_type} 
                    onValueChange={(v) => setFormData({ ...formData, wage_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WAGE_TYPES.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Rate ({formData.currency})</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={formData.wage_amount}
                      onChange={(e) => setFormData({ ...formData, wage_amount: parseFloat(e.target.value) || "" })}
                      placeholder="50"
                      className="flex-1"
                    />
                    <Select 
                      value={formData.currency} 
                      onValueChange={(v) => setFormData({ ...formData, currency: v })}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {/* External Links - for Vendors and Studios */}
            {(formData.resource_type === "VENDOR" || formData.resource_type === "STUDIO") && (
              <div className="col-span-2">
                <Label>External Links</Label>
                <div className="space-y-2 mt-2">
                  {(formData.external_links || []).map((link, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        placeholder="Label (e.g., Website, Dropbox)"
                        value={link.label}
                        onChange={(e) => {
                          const newLinks = [...formData.external_links];
                          newLinks[idx].label = e.target.value;
                          setFormData({ ...formData, external_links: newLinks });
                        }}
                        className="w-1/3"
                      />
                      <Input
                        placeholder="URL"
                        value={link.url}
                        onChange={(e) => {
                          const newLinks = [...formData.external_links];
                          newLinks[idx].url = e.target.value;
                          setFormData({ ...formData, external_links: newLinks });
                        }}
                        className="flex-1"
                      />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => {
                          const newLinks = formData.external_links.filter((_, i) => i !== idx);
                          setFormData({ ...formData, external_links: newLinks });
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData({ 
                      ...formData, 
                      external_links: [...(formData.external_links || []), { label: "", url: "" }] 
                    })}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Link
                  </Button>
                </div>
              </div>
            )}

            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!formData.display_name}>
              {editingResource ? "Update" : "Create"} Resource
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Artist Dialog */}
      <Dialog open={!!inviteArtist} onOpenChange={() => { setInviteArtist(null); setInviteEmail(""); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Invite Artist to Platform</DialogTitle>
            <DialogDescription>
              Send an invite to {inviteArtist?.display_name} to access the Artist Portal
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={inviteArtist?.display_name || ""} disabled className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input 
                type="email"
                placeholder="email@therenderpros.com"
                value={inviteEmail || inviteArtist?.email || ""}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Input 
                value={inviteArtist?.resource_type === "PM" || inviteArtist?.resource_type === "PROJECT_MANAGER" ? "Program Manager" : "Artist"} 
                disabled 
                className="bg-muted" 
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setInviteArtist(null); setInviteEmail(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={async () => {
                const email = inviteEmail || inviteArtist?.email;
                if (!email) {
                  toast.error("Email is required");
                  return;
                }
                try {
                  const response = await apiClient.post("/artists/invite", {
                    artist_id: inviteArtist.id,
                    email: email,
                    resource_type: inviteArtist.resource_type
                  });
                  
                  if (response.data.linked) {
                    toast.success(`Existing user linked to ${inviteArtist.display_name}`);
                  } else if (response.data.email_sent) {
                    toast.success(`Invitation email sent to ${email}`);
                  } else if (response.data.temp_password) {
                    // Email failed but user was created - show temp password
                    toast.warning(`User created but email failed. Temporary password: ${response.data.temp_password}`);
                  } else {
                    toast.success(`Invite created for ${inviteArtist.display_name}`);
                  }
                  
                  setInviteArtist(null);
                  setInviteEmail("");
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Failed to send invite");
                }
              }}
            >
              <Send className="w-4 h-4 mr-2" />
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" /> Archive Resources
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive {selectedIds.length} resource(s)? They will be moved to the Archived section and can be restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-destructive hover:bg-destructive/90" 
              onClick={handleBulkDelete}
              disabled={deleting}
            >
              {deleting ? "Archiving..." : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recover Deleted Resource Dialog */}
      <Dialog open={showRecoverDialog} onOpenChange={setShowRecoverDialog}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-primary" />
              Recover Deleted Resource
            </DialogTitle>
            <DialogDescription>
              Re-create a deleted resource to restore all their historical cost and timeline data.
            </DialogDescription>
          </DialogHeader>

          {!selectedOrphan ? (
            <div className="space-y-3 py-2">
              {orphanedResources.length === 0 ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    No orphaned references auto-detected. Use manual recovery to re-create a deleted resource:
                  </p>
                  <button
                    onClick={() => setSelectedOrphan({ resource_id: "", last_known_name: "", cost_log_count: 0, timeline_blocks: 0, manual: true })}
                    className="w-full flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-lg hover:bg-primary/10 transition-colors text-left"
                    data-testid="manual-recover-btn"
                  >
                    <div>
                      <p className="font-medium text-sm">Manual Recovery</p>
                      <p className="text-xs text-muted-foreground">
                        Create a new resource profile for a previously deleted team member
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Found {orphanedResources.length} deleted resource(s) with data that can be recovered:
                  </p>
                  {orphanedResources.map(o => (
                    <button
                      key={o.resource_id}
                      onClick={() => {
                        setSelectedOrphan(o);
                        setRecoverForm(f => ({ ...f, display_name: o.last_known_name !== "Unknown" ? o.last_known_name : "" }));
                      }}
                      className="w-full flex items-center justify-between p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                      data-testid={`orphan-${o.resource_id}`}
                    >
                      <div>
                        <p className="font-medium text-sm">{o.last_known_name || "Unknown Resource"}</p>
                        <p className="text-xs text-muted-foreground">
                          {o.cost_log_count} cost entries · {o.total_hours}h logged · {o.timeline_blocks} timeline blocks
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {!selectedOrphan.manual && (
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                  Recovering: <strong>{selectedOrphan.cost_log_count}</strong> cost entries,{" "}
                  <strong>{selectedOrphan.timeline_blocks}</strong> timeline blocks
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={recoverForm.display_name} onChange={e => setRecoverForm(f => ({ ...f, display_name: e.target.value }))} placeholder="Bruno Corigliano" required />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={recoverForm.email} onChange={e => setRecoverForm(f => ({ ...f, email: e.target.value }))} placeholder="name@company.com" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={recoverForm.resource_type} onValueChange={v => setRecoverForm(f => ({ ...f, resource_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ARTIST">Artist</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                      <SelectItem value="ADMIN">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Specialty</Label>
                  <Input value={recoverForm.specialty} onChange={e => setRecoverForm(f => ({ ...f, specialty: e.target.value }))} placeholder="e.g., Exteriors" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Wage Type</Label>
                  <Select value={recoverForm.wage_type} onValueChange={v => setRecoverForm(f => ({ ...f, wage_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Retainer">Retainer</SelectItem>
                      <SelectItem value="Hourly">Hourly</SelectItem>
                      <SelectItem value="Project">Project</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Rate</Label>
                  <Input type="number" value={recoverForm.wage_amount} onChange={e => setRecoverForm(f => ({ ...f, wage_amount: parseFloat(e.target.value) || 0 }))} placeholder="2400" />
                </div>
                <div className="space-y-2">
                  <Label>Monthly Hours</Label>
                  <Input type="number" value={recoverForm.contracted_hours_monthly} onChange={e => setRecoverForm(f => ({ ...f, contracted_hours_monthly: parseInt(e.target.value) || 160 }))} />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {selectedOrphan && (
              <Button variant="outline" onClick={() => setSelectedOrphan(null)} className="mr-auto">
                Back
              </Button>
            )}
            <Button variant="outline" onClick={() => { setShowRecoverDialog(false); setSelectedOrphan(null); }}>
              Cancel
            </Button>
            {selectedOrphan && (
              <Button onClick={handleRecover} disabled={recovering || !recoverForm.display_name} data-testid="confirm-recover-btn">
                {recovering ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Recovering...</> : <><RotateCcw className="w-4 h-4 mr-1" /> Recover Resource</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
